/** CRM-4 CRMConsentsPage — gestione consensi GDPR + mandato */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, Shield, Download, AlertTriangle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const TIPI = ["privacy_data", "mandato", "marketing", "whatsapp",
                "pagamenti_digitali", "comparazione_compagnie"];

const STATO_COLOR = {
  concesso: "bg-emerald-100 text-emerald-700",
  negato: "bg-rose-100 text-rose-700",
  revocato: "bg-zinc-200 text-zinc-500",
};

export default function CRMConsentsPage() {
  const [custQuery, setCustQuery] = useState("");
  const [custResults, setCustResults] = useState([]);
  const [selectedCust, setSelectedCust] = useState(null);
  const [consents, setConsents] = useState({});
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (custQuery.length < 2) { setCustResults([]); return; }
    const t = setTimeout(() => {
      axios.get(`${API}/api/admin/crm/customers/search?q=${encodeURIComponent(custQuery)}`, { withCredentials: true })
        .then((r) => setCustResults(r.data.items || []));
    }, 250);
    return () => clearTimeout(t);
  }, [custQuery]);

  const loadConsents = useCallback(async (id) => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/crm/customers/${id}/consents`, { withCredentials: true });
      setConsents(r.data.consents || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  const selectCust = (c) => {
    setSelectedCust(c); setCustResults([]); setCustQuery(c.anagrafica?.email || "");
    loadConsents(c.id);
  };

  const register = async (tipo, stato) => {
    if (!selectedCust) return;
    await axios.post(`${API}/api/admin/crm/customers/${selectedCust.id}/consents`, {
      tipo, stato,
      versione_documento: `v2026.05.${tipo}`,
      acquisito_da: "operatore",
    }, { withCredentials: true });
    loadConsents(selectedCust.id);
  };

  const revoke = async (consentId) => {
    if (!selectedCust) return;
    const motivo = prompt("Motivo revoca?") || "operatore";
    await axios.patch(`${API}/api/admin/crm/customers/${selectedCust.id}/consents/${consentId}/revoke?motivo=${encodeURIComponent(motivo)}`,
      {}, { withCredentials: true });
    loadConsents(selectedCust.id);
  };

  const gdprExport = async () => {
    if (!selectedCust) return;
    const r = await axios.get(`${API}/api/admin/crm/customers/${selectedCust.id}/gdpr-export`, { withCredentials: true });
    const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `gdpr_export_${selectedCust.id}_${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
  };

  const gdprErase = async () => {
    if (!selectedCust) return;
    if (!confirm("ATTENZIONE: anonimizza permanentemente i dati. Procedere?")) return;
    if (!confirm("Conferma ULTERIORE: i dati non saranno più recuperabili. OK?")) return;
    await axios.delete(`${API}/api/admin/crm/customers/${selectedCust.id}/gdpr-erase`, { withCredentials: true });
    alert("Cliente anonimizzato (Art. 17 GDPR)");
    setSelectedCust(null);
  };

  const generaMandato = async () => {
    if (!selectedCust) return;
    const r = await axios.post(`${API}/api/admin/crm/customers/${selectedCust.id}/mandato/genera-pdf`, {}, { withCredentials: true });
    alert(`Mandato ${r.data.version} generato (document_id=${r.data.document_id})\nDISCLAIMER: ${r.data.disclaimer}`);
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="crm-consents-page">
      <h2 className="text-xl font-semibold flex items-center gap-2"><Shield className="w-5 h-5" /> Consensi GDPR & Mandato Digitale</h2>
      <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 flex items-start gap-1">
        <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
        Mandato Digitale v2026.05 — <strong>DRAFT</strong>, richiede revisione legale prima del go-live.
      </p>

      <div className="relative">
        <input value={custQuery} onChange={(e) => { setCustQuery(e.target.value); setSelectedCust(null); }}
          placeholder="Cerca cliente (nome/email)..."
          className="w-full px-3 py-2 text-sm rounded border border-zinc-200"
          data-testid="consents-cust-search" />
        {custResults.length > 0 && !selectedCust && (
          <div className="absolute z-10 bg-white border border-zinc-200 rounded shadow w-full max-h-40 overflow-y-auto">
            {custResults.map((c) => (
              <button key={c.id} onClick={() => selectCust(c)}
                className="block w-full text-left px-2 py-1 text-xs hover:bg-zinc-50">
                {c.anagrafica?.ragione_sociale || `${c.anagrafica?.nome || ""} ${c.anagrafica?.cognome || ""}`}
                <span className="text-zinc-400 ml-2">{c.anagrafica?.email}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedCust && (
        <div className="border border-zinc-200 rounded-xl p-4 space-y-3" data-testid="consents-cust-detail">
          <p className="text-sm font-semibold">{selectedCust.anagrafica?.ragione_sociale || `${selectedCust.anagrafica?.nome} ${selectedCust.anagrafica?.cognome}`}</p>
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> :
            <div className="space-y-2">
              {TIPI.map((t) => {
                const c = consents[t];
                return (
                  <div key={t} className="flex items-center justify-between border border-zinc-100 rounded p-2 text-xs">
                    <div>
                      <p className="font-medium">{t}</p>
                      {c ? (
                        <p className="text-zinc-500">
                          <span className={`px-1 py-0.5 rounded ${STATO_COLOR[c.stato] || "bg-zinc-100"}`}>{c.stato}</span>
                          {" · "}{c.versione_documento} · {(c.acquisito_at || "").slice(0, 10)} · {c.acquisito_da}
                        </p>
                      ) : <p className="text-zinc-400 italic">Nessun consenso registrato</p>}
                    </div>
                    <div className="flex gap-1">
                      <button onClick={() => register(t, "concesso")} className="px-2 py-1 text-[10px] bg-emerald-100 text-emerald-700 rounded" data-testid={`consent-grant-${t}`}>Concedi</button>
                      <button onClick={() => register(t, "negato")} className="px-2 py-1 text-[10px] bg-rose-100 text-rose-700 rounded">Nega</button>
                      {c && c.stato === "concesso" && !c.revocato_at && (
                        <button onClick={() => revoke(c.id)} className="px-2 py-1 text-[10px] bg-zinc-200 text-zinc-700 rounded">Revoca</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>}

          <div className="flex flex-wrap gap-2 pt-2 border-t border-zinc-100">
            <button onClick={generaMandato} className="px-3 py-2 text-xs bg-zinc-900 text-white rounded" data-testid="mandato-gen-btn">
              Genera Mandato Digitale
            </button>
            <button onClick={gdprExport} className="px-3 py-2 text-xs bg-sky-100 text-sky-700 rounded flex items-center gap-1" data-testid="gdpr-export-btn">
              <Download className="w-3 h-3" /> Export GDPR (Art.15)
            </button>
            <button onClick={gdprErase} className="px-3 py-2 text-xs bg-rose-100 text-rose-700 rounded" data-testid="gdpr-erase-btn">
              Diritto Oblio (Art.17) — admin only
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
